import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import { trackOnboardingStart } from '@/lib/posthog';
import { AUTO_SETUP_ROUTE, MANUAL_SETUP_ROUTE } from './onboardingFlowLogic';

const MacroSetupChoice = () => {
  const navigate = useNavigate();

  // Track onboarding start when user lands on first onboarding page
  useEffect(() => {
    trackOnboardingStart();
  }, []);

  return (
    <div className="min-h-screen flex flex-col header-safe-top px-6 pb-6">
      {/* Back button: planted at safe-area top to match other onboarding
          screens. Routes back to /login so user can switch accounts or
          retreat from onboarding. */}
      <button
        onClick={() => navigate('/login?switch=1')}
        data-testid="setup-back-btn"
        className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors self-start"
      >
        <ChevronLeft size={18} /> Back
      </button>

      {/* Centred body — preserves the original "choose your path" framing. */}
      <div className="flex-1 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full space-y-8"
        >
          <div className="text-center space-y-2">
            <h1 className="font-heading font-bold text-3xl" data-testid="setup-title">How would you like to set your macros?</h1>
            <p className="text-muted-foreground">Choose your preferred setup method</p>
          </div>

          <div className="space-y-4">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(AUTO_SETUP_ROUTE.path, { state: AUTO_SETUP_ROUTE.state })}
              data-testid="generate-macros-option"
              className="bg-card border border-border/40 rounded-xl p-6 cursor-pointer hover:border-accent/50 transition-all"
            >
              <h3 className="font-heading font-semibold text-lg mb-2">Generate my plan</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Answer a few questions and we'll calculate personalized macro targets for your goals using established nutrition formulas
              </p>
              <div className="flex items-center justify-end text-accent">
                <span className="text-sm font-medium">Recommended</span>
                <ArrowRight size={16} className="ml-2" />
              </div>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(MANUAL_SETUP_ROUTE.path, { state: MANUAL_SETUP_ROUTE.state })}
              data-testid="manual-macros-option"
              className="bg-card border border-border/40 rounded-xl p-6 cursor-pointer hover:border-accent/50 transition-all"
            >
              <h3 className="font-heading font-semibold text-lg mb-2">Enter manually</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Already know your macros? Enter them directly
              </p>
              <div className="flex items-center justify-end text-muted-foreground">
                <span className="text-sm">For experienced users</span>
                <ArrowRight size={16} className="ml-2" />
              </div>
            </motion.div>
          </div>

          <button
            onClick={() => navigate('/sources')}
            data-testid="setup-sources-link"
            className="block mx-auto text-xs text-muted-foreground hover:text-accent underline transition-colors"
          >
            How Fitxess calculates your plan · Sources &amp; disclaimer
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default MacroSetupChoice;
